import { motion } from 'framer-motion';
import { TrendingUp, Star, Users, Award, Sparkles } from 'lucide-react';

export function BlogCoverSuccessStories() {
  const stats = [
    { value: '+1.2', label: 'pts', icon: Star, color: 'text-yellow-400', delay: 0.1 },
    { value: 'x3', label: 'avis', icon: TrendingUp, color: 'text-emerald-400', delay: 0.2 },
    { value: '95%', label: 'temps', icon: Sparkles, color: 'text-primary', delay: 0.3 },
  ];

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center overflow-hidden relative">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
      </div>
      
      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-yellow-400/10 rounded-full blur-3xl" />
      
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-4 px-4">
        {/* Success badge */}
        <motion.div 
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, type: "spring" }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-primary rounded-full blur-lg opacity-50 animate-pulse" />
          <div className="relative w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-400 via-emerald-500 to-primary rounded-full flex items-center justify-center shadow-2xl border-2 border-white/20">
            <Award className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
        </motion.div>
        
        {/* Stats row */}
        <div className="flex items-center gap-3 md:gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: stat.delay, duration: 0.3 }}
              className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-2.5 md:p-3 border border-slate-700/50 text-center min-w-[70px] md:min-w-[80px]"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <stat.icon className={`w-3 h-3 ${stat.color}`} />
              </div>
              <div className={`text-lg md:text-xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-[8px] md:text-[9px] text-slate-400 uppercase tracking-wide">{stat.label}</div>
            </motion.div>
          ))}
        </div>
        
        {/* Client avatars */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
          className="flex items-center"
        >
          <div className="flex -space-x-2">
            {[...Array(4)].map((_, i) => (
              <div 
                key={i}
                className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-800 flex items-center justify-center"
              >
                <Users className="w-3.5 h-3.5 text-slate-400" />
              </div>
            ))}
          </div>
          <div className="ml-2 bg-slate-800/60 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-700/50">
            <span className="text-[9px] text-slate-300 font-medium">+500 pros</span>
          </div>
        </motion.div>
        
        {/* Testimonial preview cards */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.3 }}
          className="flex gap-2 mt-1"
        >
          {[
            { rating: '4.7', before: '3.2', sector: 'Garage' },
            { rating: '4.9', before: '4.1', sector: 'Dentiste' },
          ].map((item, i) => (
            <div 
              key={i}
              className="bg-slate-800/60 backdrop-blur-sm rounded-lg p-2 border border-slate-700/50"
            >
              <div className="flex items-center gap-1 mb-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <span className="text-xs font-bold text-white">{item.rating}</span>
                <span className="text-[8px] text-slate-500">({item.before})</span>
              </div>
              <div className="text-[8px] text-slate-400">{item.sector}</div>
            </div>
          ))}
        </motion.div>
      </div>
      
      {/* Corner decorations */}
      <div className="absolute top-4 right-4 flex items-center gap-1 bg-emerald-500/20 backdrop-blur-sm px-2 py-1 rounded-full border border-emerald-500/30">
        <TrendingUp className="w-3 h-3 text-emerald-400" />
        <span className="text-[8px] text-emerald-400 font-medium">+40%</span>
      </div>
      
      <div className="absolute bottom-4 left-4 text-[8px] text-slate-500">
        Résultats vérifiés
      </div>
    </div>
  );
}
